import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { useRef } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import QRCode from "qrcode";
import DxfWriter from "dxf-writer";
import { useTranslation } from "react-i18next";

const pngSize = 512;
const widthRation = 0.85 * 0.35;
const heightRation = 0.85 * 0.48;
const level = "H";

export function GattiQrCode({
  value,
  size,
  fileName,
}: {
  value: string;
  size: number;
  fileName: string;
}) {
  const { t } = useTranslation();
  const divRef = useRef<HTMLCanvasElement | undefined>();
  const svgRef = useRef<Node | undefined>();

  function copyToClipboard() {
    const canvas = divRef?.current?.querySelector("canvas") as
      | HTMLCanvasElement
      | undefined;
    if (!canvas) {
      return;
    }

    canvas.toBlob(function (blob) {
      if (!blob) {
        return;
      }

      const item = new ClipboardItem({ "image/png": blob });
      navigator.clipboard
        .write([item])
        .then(function () {
          toast.success(t("qrCodeDialog.toast.copiato"));
        })
        .catch(function (error) {
          console.error(t("qrCodeDialog.errori.copiaImmagine"), error);
        });
    });
  }

  function downloadAsPng() {
    const canvas = divRef?.current?.querySelector("canvas") as
      | HTMLCanvasElement
      | undefined;
    if (!canvas) {
      return;
    }

    canvas.toBlob(function (blob) {
      if (!blob) {
        return;
      }

      const anchor = document.createElement("a");
      anchor.href = canvas.toDataURL();
      anchor.download = `${fileName}.png`;
      anchor.click();
    });
  }

  function downloadAsSvg() {
    const svg = svgRef.current;

    if (!svg) {
      return;
    }

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    // Add XML declaration (optional but recommended)
    const svgBlobContent = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n${svgString}`;

    // Create a Blob from the SVG string
    const blob = new Blob([svgBlobContent], { type: "image/svg+xml" });

    // Create an anchor element for downloading
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = `${fileName}.svg`;

    // Append the anchor to the body (necessary for some browsers)
    document.body.appendChild(a);

    // Trigger a click event to start the download
    a.click();

    // Clean up: Remove the anchor and revoke the blob URL
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function generateDxf() {
    try {
      // Generate QR code matrix data
      const qrMatrix = await QRCode.create(value, { 
        errorCorrectionLevel: level
      });

      // Create DXF writer instance
      const dxf = new DxfWriter();
      
      // Get QR code matrix
      const modules = qrMatrix.modules;
      const moduleCount = modules.size;
      const moduleSize = 1; // 1 unit per module - can be scaled in CAD software
      
      // Draw filled rectangles for dark modules
      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (modules.get(row, col)) {
            // Calculate rectangle coordinates
            const x = col * moduleSize;
            const y = (moduleCount - row - 1) * moduleSize; // Flip Y axis for proper orientation
            
            // Create a filled rectangle as a closed polyline (4-point polygon)
            dxf.drawPolyline([
              [x, y],                           // Bottom-left corner
              [x + moduleSize, y],              // Bottom-right corner  
              [x + moduleSize, y + moduleSize], // Top-right corner
              [x, y + moduleSize]               // Top-left corner
            ], true); // true = closed polyline
          }
        }
      }
      
      // Generate DXF content
      const dxfContent = dxf.toDxfString();
      
      // Create blob and download
      const blob = new Blob([dxfContent], { type: "application/dxf" });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.dxf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(t("qrCodeDialog.toast.dxfSuccess"));
    } catch (error) {
      console.error(t("qrCodeDialog.errori.dxfGenerazione"), error);
      toast.error(t("qrCodeDialog.toast.dxfError"));
    }
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <QRCodeSVG
          value={value}
          size={size}
          level={level}
          ref={svgRef as any}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            gap: 1,
            padding: 0.5,
          }}
          style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
        >
          <Tooltip title={t("qrCodeDialog.tooltip.png")}>
            <Box>
              <Button
                size="small"
                sx={{ color: "gray" }}
                onClick={downloadAsPng}
                startIcon={<DownloadIcon />}
              >
                {t("qrCodeDialog.formati.png")}
              </Button>
            </Box>
          </Tooltip>

          <Tooltip title={t("qrCodeDialog.tooltip.svg")}>
            <Button
              size="small"
              sx={{ color: "gray" }}
              onClick={downloadAsSvg}
              startIcon={<DownloadIcon />}
            >
              {t("qrCodeDialog.formati.svg")}
            </Button>
          </Tooltip>

          <Tooltip title={t("qrCodeDialog.tooltip.dxf")}>
            <Button
              size="small"
              sx={{ color: "gray" }}
              onClick={generateDxf}
              startIcon={<DownloadIcon />}
            >
              {t("qrCodeDialog.formati.dxf")}
            </Button>
          </Tooltip>

          <Tooltip title={t("qrCodeDialog.tooltip.copia")}>
            <IconButton size="small" color="primary" onClick={copyToClipboard}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <div style={{ display: "none" }} ref={divRef as any}>
        <QRCodeCanvas value={value} size={pngSize} level={level} />
      </div>
    </>
  );
}
